/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Administrator switches for the UI v2 calling bar, shown on the Twilio setup
 * page. Self-contained: it reads the two switches from /telephony/status and
 * writes only those two fields through PUT /telephony/settings, so the page's
 * own save flow is untouched.
 */

import { useEffect, useState } from "react";
import { Card, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import { isUiV2Enabled } from "hooks/uiVersion";
import { getApiErrorMessage } from "utils/apiErrors";
import PropTypes from "prop-types";
import { SettingsSection, ToggleRow } from "components/v2/settings/SettingsBlocks";

const FIELDS = [
  {
    key: "softphone_bar_enabled",
    title: "Show the calling bar",
    body: "Docked along the bottom of every page for people with a calling seat, in the new look. Turn it off to remove it for everyone; calls still work from records and the Call Console.",
  },
  {
    key: "softphone_allow_user_hide",
    title: "Let people hide it for themselves",
    body: "Each person can switch the bar off from their avatar menu, for example if they rarely call. A ringing or live call always shows it.",
  },
];

export default function SoftphoneBarSettingsCard({ variant }) {
  const { axiosInstance } = useAuth();
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/telephony/status")
      .then(({ data }) => {
        if (!cancelled) {
          setValues({
            softphone_bar_enabled: data?.softphone_bar_enabled !== false,
            softphone_allow_user_hide: data?.softphone_allow_user_hide !== false,
          });
        }
      })
      .catch(() => !cancelled && setValues({ softphone_bar_enabled: true, softphone_allow_user_hide: true }));
    return () => {
      cancelled = true;
    };
  }, [axiosInstance]);

  if (!isUiV2Enabled() || !values) return null;

  const toggle = async (key, next) => {
    setSaving(key);
    setError("");
    setValues((v) => ({ ...v, [key]: next }));
    try {
      const { data } = await axiosInstance.put("/telephony/settings", { [key]: next });
      setValues((v) => ({ ...v, [key]: data?.[key] !== false }));
    } catch (err) {
      setValues((v) => ({ ...v, [key]: !next }));
      setError(getApiErrorMessage(err, "Could not save the calling bar setting."));
    } finally {
      setSaving(null);
    }
  };

  // v2 (design reference "Settings · Phone and softphone"): the Softphone
  // section with one switch row per setting. Same reads and writes.
  if (variant === "v2") {
    return (
      <SettingsSection title="Softphone" summary="The calling bar docked at the bottom of every page for people with a calling seat.">
        {FIELDS.map((f, i) => (
          <ToggleRow
            key={f.key}
            first={i === 0}
            title={f.title}
            description={f.body}
            checked={values[f.key]}
            disabled={saving !== null || (f.key === "softphone_allow_user_hide" && !values.softphone_bar_enabled)}
            onChange={(next) => toggle(f.key, next)}
          />
        ))}
        {error && (
          <MDTypography variant="caption" color="error" display="block" pb={1}>
            {error}
          </MDTypography>
        )}
      </SettingsSection>
    );
  }

  return (
    <Card sx={{ p: 3, border: "1px solid #E6E8F0", boxShadow: "none", borderRadius: "12px", mb: 3 }}>
      <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
        Calling bar (new look)
      </MDTypography>
      <MDTypography variant="caption" color="text" display="block" mb={2}>
        Applies to people using the new look. The classic softphone is unchanged.
      </MDTypography>
      {FIELDS.map((f) => (
        <MDBox key={f.key} display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={1.5}>
          <MDBox>
            <MDTypography variant="button" fontWeight="medium" display="block">
              {f.title}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {f.body}
            </MDTypography>
          </MDBox>
          <Switch
            checked={values[f.key]}
            disabled={saving !== null || (f.key === "softphone_allow_user_hide" && !values.softphone_bar_enabled)}
            onChange={(e) => toggle(f.key, e.target.checked)}
            inputProps={{ "aria-label": f.title }}
          />
        </MDBox>
      ))}
      {error && (
        <MDTypography variant="caption" color="error">
          {error}
        </MDTypography>
      )}
    </Card>
  );
}

SoftphoneBarSettingsCard.propTypes = { variant: PropTypes.oneOf(["classic", "v2"]) };
SoftphoneBarSettingsCard.defaultProps = { variant: "classic" };
